import { startTransition, useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  apiClient,
  type IncidentEvidenceBundle,
  type IncidentExportPreparationResult,
  type IncidentExportRecord,
  type IncidentFnolBundle,
  type IncidentFnolRecord,
  type OperatorIncidentRecord,
} from "@/lib/api/client"
import {
  formatDateTime,
  INPUT_CLASS,
  matchesFilterText,
  safeJsonStringify,
  shortId,
  TEXTAREA_CLASS,
  titleizeToken,
} from "@/features/operator/advanced/advanced-ops-utils"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  resolveOperatorSession,
  type OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"

type ExportType =
  | "evidence"
  | "government_share"
  | "institutional_summary"
  | "insurance_fnol"

type FnolIncidentRecord = Omit<OperatorIncidentRecord, "exportPackages"> & {
  exportPackages?: IncidentExportRecord[]
  fnol?: IncidentFnolRecord | null
  reportedAt?: string | null
}

export interface ExportsFnolSearch {
  exportType?: "all" | ExportType
  incidentId?: string
  q?: string
  readiness?: "all" | "missing_required_fields" | "ready" | "submitted"
}

interface ExportsFnolWorkspaceProps {
  onSearchChange: (next: Partial<ExportsFnolSearch>) => void
  search: ExportsFnolSearch
}

interface FnolWorkspaceBundle {
  incidents: FnolIncidentRecord[]
  session: OperatorResolvedSession
  stats: {
    in_progress?: number
    pending?: number
    resolved?: number
    total?: number
  } | null
}

interface FnolAnalysis {
  previewLabel: string
  previewTone: "missing_required_fields" | "ready" | "submitted"
  requiredMissing: Array<{
    key: string
    label: string
  }>
}

function canAccessFnolWorkspace(session: OperatorResolvedSession) {
  return Boolean(session.permissions.manageExports || session.permissions.exportEvidence)
}

async function loadFnolWorkspaceBundle(): Promise<FnolWorkspaceBundle> {
  const session = await resolveOperatorSession()

  if (!canAccessFnolWorkspace(session)) {
    throw new Error("This session does not have access to the FNOL desk.")
  }

  const [statsResult, incidentsResult] = await Promise.all([
    apiClient.getOperatorIncidentStats(),
    apiClient.getOperatorIncidents({
      limit: 120,
    }),
  ])

  return {
    incidents: (incidentsResult.data ?? []) as FnolIncidentRecord[],
    session,
    stats: statsResult.data ?? null,
  }
}

function createEmptyDraft(): IncidentFnolRecord {
  return {
    claimReference: "",
    contactProfile: {
      email: "",
      name: "",
      phone: "",
    },
    coverageDetails: {
      adjusterName: "",
      adjusterPhone: "",
      coverageType: "",
      notes: "",
    },
    damageSummary: {
      description: "",
      severity: "",
    },
    driverProfile: {
      email: "",
      emergencyContactName: "",
      emergencyContactPhone: "",
      licenseNumber: "",
      name: "",
      phone: "",
    },
    fleetDetails: {
      assigneeName: "",
      externalRef: "",
      fleetCategory: "",
      fleetName: "",
      memberType: "",
      organizationId: "",
      organizationName: "",
    },
    injurySummary: {
      injuryStatus: "",
      medicalProfile: "",
      peopleInvolved: "",
    },
    insurerCode: "",
    insurerName: "",
    locationLabel: "",
    lossType: "",
    metadata: {
      internalNotes: "",
      packageOwner: "",
      reviewTag: "",
    },
    mode: "consumer",
    narrative: "",
    occurredAt: "",
    policyNumber: "",
    reportedAt: "",
    thirdPartyProfile: {
      name: "",
      notes: "",
      phone: "",
      vehicleDescription: "",
    },
    vehicleProfile: {
      color: "",
      make: "",
      model: "",
      plateNumber: "",
      tag: "",
      type: "",
      year: "",
    },
  }
}

function buildFnolDraft(source?: IncidentFnolRecord | null): IncidentFnolRecord {
  const base = createEmptyDraft()

  return {
    ...base,
    ...source,
    contactProfile: {
      ...(base.contactProfile ?? {}),
      ...(source?.contactProfile ?? {}),
    },
    coverageDetails: {
      ...(base.coverageDetails ?? {}),
      ...(source?.coverageDetails ?? {}),
    },
    damageSummary: {
      ...(base.damageSummary ?? {}),
      ...(source?.damageSummary ?? {}),
    },
    driverProfile: {
      ...(base.driverProfile ?? {}),
      ...(source?.driverProfile ?? {}),
    },
    fleetDetails: {
      ...(base.fleetDetails ?? {}),
      ...(source?.fleetDetails ?? {}),
    },
    injurySummary: {
      ...(base.injurySummary ?? {}),
      ...(source?.injurySummary ?? {}),
    },
    metadata: {
      ...(base.metadata ?? {}),
      ...(source?.metadata ?? {}),
    },
    thirdPartyProfile: {
      ...(base.thirdPartyProfile ?? {}),
      ...(source?.thirdPartyProfile ?? {}),
    },
    vehicleProfile: {
      ...(base.vehicleProfile ?? {}),
      ...(source?.vehicleProfile ?? {}),
    },
  }
}

function setValueAtPath(target: Record<string, unknown>, path: string, value: unknown) {
  const segments = path.split(".")
  let pointer: Record<string, unknown> = target

  segments.slice(0, -1).forEach((segment) => {
    const current = pointer[segment]
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      pointer[segment] = {}
    }

    pointer = pointer[segment] as Record<string, unknown>
  })

  pointer[segments[segments.length - 1]] = value
}

function cleanString(value: unknown) {
  return typeof value === "string" ? value.trim() : ""
}

function normalizeObject(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {}
  }

  return Object.entries(value).reduce<Record<string, unknown>>((accumulator, [key, nestedValue]) => {
    if (nestedValue && typeof nestedValue === "object" && !Array.isArray(nestedValue)) {
      accumulator[key] = normalizeObject(nestedValue)
      return accumulator
    }

    accumulator[key] = cleanString(nestedValue)
    return accumulator
  }, {})
}

function toInputValue(value: unknown) {
  if (value === null || value === undefined) {
    return ""
  }

  return String(value)
}

function analyzeFnolDraft(draft: IncidentFnolRecord): FnolAnalysis {
  const requiredMissing = [
    {
      key: "coverage",
      label: "Coverage identity",
      present: Boolean(cleanString(draft.insurerName) || cleanString(draft.policyNumber)),
    },
    {
      key: "driver",
      label: "Driver identity",
      present: Boolean(
        cleanString(draft.driverProfile?.name) || cleanString(draft.contactProfile?.name)
      ),
    },
    {
      key: "vehicle",
      label: "Vehicle identity",
      present: Boolean(
        cleanString(draft.vehicleProfile?.plateNumber)
          || cleanString(draft.vehicleProfile?.make)
          || cleanString(draft.vehicleProfile?.tag)
      ),
    },
    {
      key: "loss_details",
      label: "Loss details",
      present: Boolean(cleanString(draft.lossType) || cleanString(draft.narrative)),
    },
  ].filter((item) => !item.present)

  if (
    draft.mode === "fleet"
    && !(
      cleanString(draft.fleetDetails?.organizationId)
      || cleanString(draft.fleetDetails?.fleetName)
    )
  ) {
    requiredMissing.push({
      key: "fleet",
      label: "Fleet identity",
      present: false,
    })
  }

  if (requiredMissing.length === 0) {
    return {
      previewLabel: draft.status === "submitted" ? "Submitted" : "Ready",
      previewTone: draft.status === "submitted" ? "submitted" : "ready",
      requiredMissing,
    }
  }

  return {
    previewLabel: "Missing required fields",
    previewTone: "missing_required_fields",
    requiredMissing,
  }
}

function getIncidentReadiness(incident: FnolIncidentRecord) {
  if (incident.fnol?.status === "submitted") {
    return "submitted"
  }

  if (incident.fnol?.exportReadiness === "ready") {
    return "ready"
  }

  return "missing_required_fields"
}

function buildFnolPayload(draft: IncidentFnolRecord) {
  return {
    mode: draft.mode || "consumer",
    insurerName: cleanString(draft.insurerName) || undefined,
    insurerCode: cleanString(draft.insurerCode) || undefined,
    policyNumber: cleanString(draft.policyNumber) || undefined,
    claimReference: cleanString(draft.claimReference) || undefined,
    lossType: cleanString(draft.lossType) || undefined,
    occurredAt: cleanString(draft.occurredAt) || undefined,
    reportedAt: cleanString(draft.reportedAt) || undefined,
    locationLabel: cleanString(draft.locationLabel) || undefined,
    narrative: cleanString(draft.narrative) || undefined,
    contactProfile: normalizeObject(draft.contactProfile),
    driverProfile: normalizeObject(draft.driverProfile),
    vehicleProfile: normalizeObject(draft.vehicleProfile),
    thirdPartyProfile: normalizeObject(draft.thirdPartyProfile),
    injurySummary: normalizeObject(draft.injurySummary),
    damageSummary: normalizeObject(draft.damageSummary),
    coverageDetails: normalizeObject(draft.coverageDetails),
    fleetDetails: normalizeObject(draft.fleetDetails),
    metadata: normalizeObject(draft.metadata),
  }
}

function getDefaultRecipientType(exportType: ExportType) {
  if (exportType === "insurance_fnol") {
    return "insurer"
  }

  if (exportType === "government_share") {
    return "government"
  }

  return "institutional"
}

function buildExportPayload(
  exportType: ExportType,
  draft: IncidentFnolRecord,
  formData: FormData
) {
  const payload: Record<string, unknown> = {
    exportType,
    format: "json",
    recipientKey: cleanString(formData.get("recipientKey")) || undefined,
    recipientType:
      cleanString(formData.get("recipientType")) || getDefaultRecipientType(exportType),
    redactionProfile: {
      hideMediaUrls: Boolean(formData.get("hideMediaUrls")),
      hideMedicalDetails: Boolean(formData.get("hideMedicalDetails")),
      hidePreciseLocation: Boolean(formData.get("hidePreciseLocation")),
      hideReporterContact: Boolean(formData.get("hideReporterContact")),
    },
  }

  if (exportType === "insurance_fnol") {
    payload.fnol = buildFnolPayload(draft)
    payload.submit = true
  }

  return payload
}

function mergeIncidentExport(
  incidents: FnolIncidentRecord[],
  incidentId: number,
  prepared: IncidentExportPreparationResult
) {
  return incidents.map((incident) => {
    if (incident.id !== incidentId) {
      return incident
    }

    return {
      ...incident,
      exportPackages: [prepared as IncidentExportRecord].concat(incident.exportPackages ?? []),
    }
  })
}

export function ExportsFnolWorkspace({
  onSearchChange,
  search,
}: ExportsFnolWorkspaceProps) {
  const [bundle, setBundle] = useState<FnolWorkspaceBundle | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [incidentDetails, setIncidentDetails] = useState<Record<number, FnolIncidentRecord>>({})
  const [fnolBundles, setFnolBundles] = useState<Record<number, IncidentFnolBundle>>({})
  const [evidenceBundles, setEvidenceBundles] = useState<Record<number, IncidentEvidenceBundle>>(
    {}
  )
  const [draftsByIncidentId, setDraftsByIncidentId] = useState<Record<number, IncidentFnolRecord>>(
    {}
  )
  const [dirtyIncidentIds, setDirtyIncidentIds] = useState<Record<number, boolean>>({})
  const [preparedResults, setPreparedResults] = useState<
    Record<number, IncidentExportPreparationResult>
  >({})
  const [pendingAction, setPendingAction] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    let isActive = true

    async function load() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const nextBundle = await loadFnolWorkspaceBundle()

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(nextBundle)
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the FNOL workspace."
          )
          setIsLoading(false)
        })
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [])

  const filteredIncidents = useMemo(() => {
    if (!bundle) {
      return []
    }

    return bundle.incidents.filter((incident) => {
      if (search.readiness && search.readiness !== "all" && getIncidentReadiness(incident) !== search.readiness) {
        return false
      }

      if (search.exportType && search.exportType !== "all") {
        const hasExportType = (incident.exportPackages ?? []).some(
          (pkg) => pkg.exportType === search.exportType
        )
        if (!hasExportType) {
          return false
        }
      }

      return matchesFilterText(search.q, [
        incident.id,
        incident.type,
        incident.reportKind,
        incident.locationDescription,
        incident.jurisdiction?.name,
        incident.fnol?.policyNumber,
        incident.fnol?.claimReference,
      ])
    })
  }, [bundle, search.exportType, search.q, search.readiness])

  const selectedIncident =
    filteredIncidents.find((incident) => String(incident.id) === search.incidentId)
    ?? filteredIncidents[0]
    ?? null
  const selectedIncidentId = selectedIncident?.id

  useEffect(() => {
    if (!selectedIncidentId || fnolBundles[selectedIncidentId]) {
      return
    }

    let isActive = true

    void Promise.all([
      apiClient.getOperatorIncident(selectedIncidentId),
      apiClient.getIncidentFnol(selectedIncidentId),
      apiClient.getIncidentEvidenceBundle(selectedIncidentId),
    ])
      .then(([incidentResponse, fnolResponse, evidenceResponse]) => {
        if (!isActive) {
          return
        }

        const detail = incidentResponse.data as FnolIncidentRecord

        setIncidentDetails((previous) => ({
          ...previous,
          [selectedIncidentId]: detail,
        }))
        setFnolBundles((previous) => ({
          ...previous,
          [selectedIncidentId]: fnolResponse.data,
        }))
        setEvidenceBundles((previous) => ({
          ...previous,
          [selectedIncidentId]: evidenceResponse.data,
        }))
        setDraftsByIncidentId((previous) => ({
          ...previous,
          [selectedIncidentId]:
            previous[selectedIncidentId] ?? buildFnolDraft(fnolResponse.data.fnol ?? detail.fnol),
        }))
      })
      .catch(() => {
        if (!isActive) {
          return
        }
      })

    return () => {
      isActive = false
    }
  }, [fnolBundles, selectedIncidentId])

  const selectedDetail = selectedIncidentId
    ? incidentDetails[selectedIncidentId] ?? selectedIncident
    : null
  const selectedFnolBundle = selectedIncidentId ? fnolBundles[selectedIncidentId] : null
  const selectedEvidenceBundle = selectedIncidentId ? evidenceBundles[selectedIncidentId] : null
  const draft =
    (selectedIncidentId ? draftsByIncidentId[selectedIncidentId] : null)
    ?? buildFnolDraft(selectedFnolBundle?.fnol ?? selectedDetail?.fnol)
  const analysis = analyzeFnolDraft(draft)
  const canWrite = Boolean(bundle?.session.permissions.manageExports || bundle?.session.permissions.exportEvidence)

  function updateDraft(path: string, value: string) {
    if (!selectedIncidentId) {
      return
    }

    setDraftsByIncidentId((previous) => {
      const nextDraft = buildFnolDraft(previous[selectedIncidentId] ?? draft)
      setValueAtPath(nextDraft as Record<string, unknown>, path, value)

      return {
        ...previous,
        [selectedIncidentId]: nextDraft,
      }
    })
    setDirtyIncidentIds((previous) => ({
      ...previous,
      [selectedIncidentId]: true,
    }))
  }

  async function handleSaveFnol() {
    if (!selectedIncidentId) {
      return
    }

    setActionError(null)
    setPendingAction(`save:${selectedIncidentId}`)

    try {
      const response = await apiClient.saveIncidentFnol(
        selectedIncidentId,
        buildFnolPayload(draft)
      )
      setFnolBundles((previous) => ({
        ...previous,
        [selectedIncidentId]: response.data,
      }))
      setDraftsByIncidentId((previous) => ({
        ...previous,
        [selectedIncidentId]: buildFnolDraft(response.data.fnol),
      }))
      setDirtyIncidentIds((previous) => ({
        ...previous,
        [selectedIncidentId]: false,
      }))
      setBundle((previous) =>
        previous
          ? {
              ...previous,
              incidents: previous.incidents.map((incident) =>
                incident.id === selectedIncidentId
                  ? {
                      ...incident,
                      fnol: response.data.fnol,
                    }
                  : incident
              ),
            }
          : previous
      )
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Failed to save FNOL.")
    } finally {
      setPendingAction(null)
    }
  }

  async function handlePrepareExport(exportType: ExportType, formData: FormData) {
    if (!selectedIncidentId) {
      return
    }

    setActionError(null)
    setPendingAction(`prepare:${selectedIncidentId}:${exportType}`)

    try {
      const response = await apiClient.prepareIncidentExport(
        selectedIncidentId,
        buildExportPayload(exportType, draft, formData)
      )
      setPreparedResults((previous) => ({
        ...previous,
        [selectedIncidentId]: response.data,
      }))
      setBundle((previous) =>
        previous
          ? {
              ...previous,
              incidents: mergeIncidentExport(previous.incidents, selectedIncidentId, response.data),
            }
          : previous
      )
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to prepare the export package."
      )
    } finally {
      setPendingAction(null)
    }
  }

  const readinessCounts = {
    missing_required_fields: filteredIncidents.filter(
      (incident) => getIncidentReadiness(incident) === "missing_required_fields"
    ).length,
    ready: filteredIncidents.filter((incident) => getIncidentReadiness(incident) === "ready")
      .length,
    submitted: filteredIncidents.filter(
      (incident) => getIncidentReadiness(incident) === "submitted"
    ).length,
  }

  return (
    <>
      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}
      {actionError ? (
        <OperatorStatusMessage tone="warning">{actionError}</OperatorStatusMessage>
      ) : null}

      {isLoading ? <FnolWorkspaceLoadingState /> : null}

      {!isLoading && bundle ? (
        <>
          <OperatorPanel
            title="Filters"
            description="Filter incidents by readiness, export history, and search text."
          >
            <div className="grid gap-4 lg:grid-cols-3">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Readiness</span>
                <select
                  aria-label="FNOL readiness"
                  className={INPUT_CLASS}
                  value={search.readiness ?? "all"}
                  onChange={(event) =>
                    onSearchChange({
                      readiness:
                        event.target.value === "all"
                          ? undefined
                          : (event.target.value as ExportsFnolSearch["readiness"]),
                    })
                  }
                >
                  <option value="all">All incidents</option>
                  <option value="missing_required_fields">Missing required fields</option>
                  <option value="ready">Ready</option>
                  <option value="submitted">Submitted</option>
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Prepared export type</span>
                <select
                  aria-label="Prepared export type"
                  className={INPUT_CLASS}
                  value={search.exportType ?? "all"}
                  onChange={(event) =>
                    onSearchChange({
                      exportType:
                        event.target.value === "all"
                          ? undefined
                          : (event.target.value as ExportsFnolSearch["exportType"]),
                    })
                  }
                >
                  <option value="all">Any export history</option>
                  <option value="evidence">Evidence</option>
                  <option value="insurance_fnol">Insurance FNOL</option>
                  <option value="government_share">Government share</option>
                  <option value="institutional_summary">Institutional summary</option>
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Search incidents</span>
                <input
                  aria-label="Search incidents"
                  className={INPUT_CLASS}
                  value={search.q ?? ""}
                  placeholder="Incident, policy, claim, location"
                  onChange={(event) =>
                    onSearchChange({
                      q: event.target.value || undefined,
                    })
                  }
                />
              </label>
            </div>
          </OperatorPanel>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              detail={`${bundle.stats?.pending ?? 0} pending · ${bundle.stats?.in_progress ?? 0} in progress`}
              label="Incident queue"
              value={filteredIncidents.length}
            />
            <OperatorMetricCard
              detail={`${readinessCounts.submitted} already submitted`}
              label="Ready for FNOL"
              value={readinessCounts.ready}
            />
            <OperatorMetricCard
              detail="Backend readiness gate still blocks insurance package creation."
              label="Missing required"
              value={readinessCounts.missing_required_fields}
            />
            <OperatorMetricCard
              detail={`${bundle.stats?.resolved ?? 0} resolved in current scope`}
              label="Submitted FNOL"
              value={readinessCounts.submitted}
            />
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <OperatorPanel
              title="Incident queue"
              description="The queue keeps current readiness posture and prepared export history visible so operators can work the insurer-ready backlog."
            >
              {filteredIncidents.length ? (
                <div className="grid gap-3">
                  {filteredIncidents.map((incident) => {
                    const readiness = getIncidentReadiness(incident)
                    const isActive = incident.id === selectedIncidentId

                    return (
                      <button
                        key={incident.id}
                        className={`rounded-3xl border px-4 py-4 text-left transition-colors ${
                          isActive
                            ? "border-primary/55 bg-primary/8"
                            : "border-border/70 bg-background/65 hover:border-primary/35"
                        }`}
                        type="button"
                        onClick={() =>
                          onSearchChange({
                            incidentId: String(incident.id),
                          })
                        }
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold">
                              Incident #{incident.id} · {titleizeToken(incident.type)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {incident.locationDescription
                                || incident.location?.name
                                || incident.jurisdiction?.name
                                || "Location pending"}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                              {titleizeToken(readiness)}
                            </span>
                            <span className="rounded-full bg-background px-3 py-1">
                              {titleizeToken(
                                incident.incidentLifecycle?.incidentStatus || incident.status
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                          <div>Policy {incident.fnol?.policyNumber || "Not set"}</div>
                          <div>Claim {incident.fnol?.claimReference || "Not set"}</div>
                          <div>Prepared {(incident.exportPackages ?? []).length}</div>
                          <div>Reported {formatDateTime(incident.reportedAt || incident.createdAt)}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No incidents match the current filters."
                  message="Adjust readiness, export history, or search terms to continue triaging the FNOL queue."
                />
              )}
            </OperatorPanel>

            <OperatorPanel
              title="Selected incident"
              description="FNOL fields remain editable here, while evidence and export history stay visible for insurer handoff."
            >
              {selectedDetail && selectedIncidentId ? (
                <div className="grid gap-4">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold">
                          Incident #{selectedDetail.id} · {titleizeToken(selectedDetail.type)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {selectedDetail.description
                            || selectedDetail.locationDescription
                            || "Incident description not provided."}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                          {analysis.previewLabel}
                        </span>
                        {dirtyIncidentIds[selectedIncidentId] ? (
                          <span className="rounded-full bg-background px-3 py-1">
                            Unsaved changes
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                      <div>Evidence checksum {shortId(selectedEvidenceBundle?.checksum, 12)}</div>
                      <div>Prepared packages {(selectedDetail.exportPackages ?? []).length}</div>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm font-semibold">Insurer readiness</div>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      Insurance FNOL export stays blocked until the backend-required sections
                      are present.
                    </p>
                    {analysis.requiredMissing.length ? (
                      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                        {analysis.requiredMissing.map((item) => (
                          <li key={item.key}>{item.label}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-3 text-sm text-muted-foreground">
                        The current draft satisfies every insurer-ready requirement.
                      </div>
                    )}
                  </div>

                  <FnolEditorCard
                    canWrite={canWrite}
                    draft={draft}
                    pendingAction={pendingAction}
                    selectedIncidentId={selectedIncidentId}
                    updateDraft={updateDraft}
                    onSave={() => void handleSaveFnol()}
                  />

                  <form
                    className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                    onSubmit={(event) => {
                      event.preventDefault()
                    }}
                  >
                    <div className="text-sm font-semibold">Prepare export package</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <Field label="Recipient type" name="recipientType" defaultValue="insurer" />
                      <Field label="Recipient key" name="recipientKey" />
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                      <CheckboxField label="Hide reporter contact" name="hideReporterContact" />
                      <CheckboxField label="Hide medical details" name="hideMedicalDetails" />
                      <CheckboxField label="Hide media URLs" name="hideMediaUrls" />
                      <CheckboxField label="Hide precise location" name="hidePreciseLocation" />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-3">
                      {(
                        [
                          "evidence",
                          "insurance_fnol",
                          "government_share",
                          "institutional_summary",
                        ] as ExportType[]
                      ).map((exportType) => {
                        const blocked =
                          exportType === "insurance_fnol"
                          && analysis.previewTone === "missing_required_fields"
                        const actionKey = `prepare:${selectedIncidentId}:${exportType}`

                        return (
                          <Button
                            key={exportType}
                            disabled={!canWrite || blocked || pendingAction === actionKey}
                            onClick={(event) => {
                              const form = event.currentTarget.form
                              if (!form) {
                                return
                              }

                              void handlePrepareExport(
                                exportType,
                                new FormData(form)
                              )
                            }}
                            type="button"
                            variant={exportType === "insurance_fnol" ? "default" : "outline"}
                          >
                            {pendingAction === actionKey
                              ? "Preparing…"
                              : `Prepare ${titleizeToken(exportType)}`}
                          </Button>
                        )
                      })}
                    </div>
                  </form>

                  {preparedResults[selectedIncidentId] ? (
                    <details className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <summary className="cursor-pointer text-sm font-semibold">
                        Latest prepared package
                      </summary>
                      <pre className="mt-3 max-h-80 overflow-auto rounded-2xl bg-background p-3 text-xs">
                        {safeJsonStringify(preparedResults[selectedIncidentId])}
                      </pre>
                    </details>
                  ) : null}

                  {selectedEvidenceBundle?.auditPackage ? (
                    <details className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <summary className="cursor-pointer text-sm font-semibold">
                        Evidence package preview
                      </summary>
                      <pre className="mt-3 max-h-80 overflow-auto rounded-2xl bg-background p-3 text-xs">
                        {safeJsonStringify(selectedEvidenceBundle.auditPackage)}
                      </pre>
                    </details>
                  ) : null}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No incident selected."
                  message="Choose an incident to inspect FNOL readiness, evidence, and export history."
                />
              )}
            </OperatorPanel>
          </div>
        </>
      ) : null}
    </>
  )
}

function FnolWorkspaceLoadingState() {
  return (
    <>
      <OperatorPanel
        title="Filters"
        description="Filter incidents by readiness, export history, and search text."
      >
        <div className="grid gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={`fnol-filter-${index}`} className="h-11 w-full rounded-2xl" />
          ))}
        </div>
      </OperatorPanel>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`fnol-metric-${index}`}
            className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
          >
            <Skeleton className="h-4 w-28" />
            <Skeleton className="mt-3 h-8 w-20" />
            <Skeleton className="mt-3 h-4 w-full" />
          </div>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <OperatorPanel
          title="Incident queue"
          description="The queue keeps current readiness posture and prepared export history visible so operators can work the insurer-ready backlog."
        >
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`fnol-incident-${index}`}
                className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="mt-2 h-4 w-32" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, detailIndex) => (
                    <Skeleton
                      key={`fnol-incident-detail-${index}-${detailIndex}`}
                      className="h-4 w-full"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </OperatorPanel>

        <OperatorPanel
          title="Selected incident"
          description="FNOL fields remain editable here, while evidence and export history stay visible for insurer handoff."
        >
          <div className="grid gap-4">
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="mt-3 h-4 w-full" />
              <Skeleton className="mt-2 h-4 w-5/6" />
            </div>
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="mt-3 h-4 w-full" />
              <Skeleton className="mt-2 h-4 w-4/5" />
            </div>
            <Skeleton className="h-80 w-full rounded-3xl" />
            <Skeleton className="h-56 w-full rounded-3xl" />
          </div>
        </OperatorPanel>
      </div>
    </>
  )
}

function FnolEditorCard({
  canWrite,
  draft,
  onSave,
  pendingAction,
  selectedIncidentId,
  updateDraft,
}: {
  canWrite: boolean
  draft: IncidentFnolRecord
  onSave: () => void
  pendingAction: string | null
  selectedIncidentId: number
  updateDraft: (path: string, value: string) => void
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-sm font-semibold">FNOL editor</div>
      <div className="mt-3 grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Mode"
            select
            value={draft.mode || "consumer"}
            onChange={(value) => updateDraft("mode", value)}
            options={[
              { label: "Consumer", value: "consumer" },
              { label: "Fleet", value: "fleet" },
            ]}
          />
          <Field
            label="Loss type"
            placeholder="collision, injury, property_damage"
            value={toInputValue(draft.lossType)}
            onChange={(value) => updateDraft("lossType", value)}
          />
          <Field
            label="Insurer name"
            placeholder="Carrier or program name"
            value={toInputValue(draft.insurerName)}
            onChange={(value) => updateDraft("insurerName", value)}
          />
          <Field
            label="Insurer code"
            placeholder="Optional carrier code"
            value={toInputValue(draft.insurerCode)}
            onChange={(value) => updateDraft("insurerCode", value)}
          />
          <Field
            label="Policy number"
            placeholder="Policy or account number"
            value={toInputValue(draft.policyNumber)}
            onChange={(value) => updateDraft("policyNumber", value)}
          />
          <Field
            label="Claim reference"
            placeholder="Optional claim reference"
            value={toInputValue(draft.claimReference)}
            onChange={(value) => updateDraft("claimReference", value)}
          />
          <Field
            label="Occurred at"
            type="datetime-local"
            value={toInputValue(draft.occurredAt)}
            onChange={(value) => updateDraft("occurredAt", value)}
          />
          <Field
            label="Reported at"
            type="datetime-local"
            value={toInputValue(draft.reportedAt)}
            onChange={(value) => updateDraft("reportedAt", value)}
          />
          <Field
            label="Location label"
            placeholder="Dispatch or insurer location label"
            value={toInputValue(draft.locationLabel)}
            onChange={(value) => updateDraft("locationLabel", value)}
          />
        </div>

        <TextareaField
          label="Narrative"
          placeholder="Summarize what happened and any operator observations."
          value={toInputValue(draft.narrative)}
          onChange={(value) => updateDraft("narrative", value)}
        />

        <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
          <div className="text-sm font-semibold">Contact profile</div>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Contact and driver sections help the backend satisfy insurer-ready coverage.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field
              label="Contact name"
              value={toInputValue(draft.contactProfile?.name)}
              onChange={(value) => updateDraft("contactProfile.name", value)}
            />
            <Field
              label="Contact phone"
              value={toInputValue(draft.contactProfile?.phone)}
              onChange={(value) => updateDraft("contactProfile.phone", value)}
            />
            <Field
              label="Contact email"
              type="email"
              value={toInputValue(draft.contactProfile?.email)}
              onChange={(value) => updateDraft("contactProfile.email", value)}
            />
            <Field
              label="Driver name"
              value={toInputValue(draft.driverProfile?.name)}
              onChange={(value) => updateDraft("driverProfile.name", value)}
            />
            <Field
              label="Driver phone"
              value={toInputValue(draft.driverProfile?.phone)}
              onChange={(value) => updateDraft("driverProfile.phone", value)}
            />
            <Field
              label="Driver email"
              type="email"
              value={toInputValue(draft.driverProfile?.email)}
              onChange={(value) => updateDraft("driverProfile.email", value)}
            />
            <Field
              label="Emergency contact name"
              value={toInputValue(draft.driverProfile?.emergencyContactName)}
              onChange={(value) => updateDraft("driverProfile.emergencyContactName", value)}
            />
            <Field
              label="Emergency contact phone"
              value={toInputValue(draft.driverProfile?.emergencyContactPhone)}
              onChange={(value) => updateDraft("driverProfile.emergencyContactPhone", value)}
            />
            <Field
              label="Driver license"
              value={toInputValue(draft.driverProfile?.licenseNumber)}
              onChange={(value) => updateDraft("driverProfile.licenseNumber", value)}
            />
          </div>
        </div>

        <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
          <div className="text-sm font-semibold">Vehicle and damage</div>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Vehicle identity drives the required insurer-ready vehicle section. Damage, injury,
            and third-party details stay optional but recommended.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field
              label="Vehicle tag"
              value={toInputValue(draft.vehicleProfile?.tag)}
              onChange={(value) => updateDraft("vehicleProfile.tag", value)}
            />
            <Field
              label="Vehicle type"
              value={toInputValue(draft.vehicleProfile?.type)}
              onChange={(value) => updateDraft("vehicleProfile.type", value)}
            />
            <Field
              label="Plate number"
              value={toInputValue(draft.vehicleProfile?.plateNumber)}
              onChange={(value) => updateDraft("vehicleProfile.plateNumber", value)}
            />
            <Field
              label="Vehicle make"
              value={toInputValue(draft.vehicleProfile?.make)}
              onChange={(value) => updateDraft("vehicleProfile.make", value)}
            />
            <Field
              label="Vehicle model"
              value={toInputValue(draft.vehicleProfile?.model)}
              onChange={(value) => updateDraft("vehicleProfile.model", value)}
            />
            <Field
              label="Year"
              value={toInputValue(draft.vehicleProfile?.year)}
              onChange={(value) => updateDraft("vehicleProfile.year", value)}
            />
            <Field
              label="Color"
              value={toInputValue(draft.vehicleProfile?.color)}
              onChange={(value) => updateDraft("vehicleProfile.color", value)}
            />
            <Field
              label="Damage severity"
              value={toInputValue(draft.damageSummary?.severity)}
              onChange={(value) => updateDraft("damageSummary.severity", value)}
            />
            <TextareaField
              label="Damage description"
              placeholder="Vehicle or property damage notes"
              value={toInputValue(draft.damageSummary?.description)}
              onChange={(value) => updateDraft("damageSummary.description", value)}
            />
            <Field
              label="Injury status"
              value={toInputValue(draft.injurySummary?.injuryStatus)}
              onChange={(value) => updateDraft("injurySummary.injuryStatus", value)}
            />
            <Field
              label="People involved"
              value={toInputValue(draft.injurySummary?.peopleInvolved)}
              onChange={(value) => updateDraft("injurySummary.peopleInvolved", value)}
            />
            <TextareaField
              label="Medical profile notes"
              placeholder="Optional medical profile summary"
              value={toInputValue(draft.injurySummary?.medicalProfile)}
              onChange={(value) => updateDraft("injurySummary.medicalProfile", value)}
            />
            <Field
              label="Third-party name"
              value={toInputValue(draft.thirdPartyProfile?.name)}
              onChange={(value) => updateDraft("thirdPartyProfile.name", value)}
            />
            <Field
              label="Third-party phone"
              value={toInputValue(draft.thirdPartyProfile?.phone)}
              onChange={(value) => updateDraft("thirdPartyProfile.phone", value)}
            />
            <Field
              label="Third-party vehicle"
              value={toInputValue(draft.thirdPartyProfile?.vehicleDescription)}
              onChange={(value) => updateDraft("thirdPartyProfile.vehicleDescription", value)}
            />
            <TextareaField
              label="Third-party notes"
              placeholder="Optional third-party notes"
              value={toInputValue(draft.thirdPartyProfile?.notes)}
              onChange={(value) => updateDraft("thirdPartyProfile.notes", value)}
            />
          </div>
        </div>

        <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
          <div className="text-sm font-semibold">Coverage and operations metadata</div>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            These optional fields help reviewers organize the case and label the package clearly.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field
              label="Coverage type"
              value={toInputValue(draft.coverageDetails?.coverageType)}
              onChange={(value) => updateDraft("coverageDetails.coverageType", value)}
            />
            <Field
              label="Adjuster name"
              value={toInputValue(draft.coverageDetails?.adjusterName)}
              onChange={(value) => updateDraft("coverageDetails.adjusterName", value)}
            />
            <Field
              label="Adjuster phone"
              value={toInputValue(draft.coverageDetails?.adjusterPhone)}
              onChange={(value) => updateDraft("coverageDetails.adjusterPhone", value)}
            />
            <TextareaField
              label="Coverage notes"
              placeholder="Optional coverage handling notes"
              value={toInputValue(draft.coverageDetails?.notes)}
              onChange={(value) => updateDraft("coverageDetails.notes", value)}
            />
            <Field
              label="Package owner"
              value={toInputValue(draft.metadata?.packageOwner)}
              onChange={(value) => updateDraft("metadata.packageOwner", value)}
            />
            <Field
              label="Review tag"
              value={toInputValue(draft.metadata?.reviewTag)}
              onChange={(value) => updateDraft("metadata.reviewTag", value)}
            />
            <TextareaField
              label="Internal notes"
              placeholder="Optional internal operator notes"
              value={toInputValue(draft.metadata?.internalNotes)}
              onChange={(value) => updateDraft("metadata.internalNotes", value)}
            />
          </div>
        </div>

        {draft.mode === "fleet" ? (
          <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
            <div className="text-sm font-semibold">Fleet details</div>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              Fleet-mode packages require a fleet identity section before insurer export can be
              prepared.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field
                label="Fleet organization id"
                value={toInputValue(draft.fleetDetails?.organizationId)}
                onChange={(value) => updateDraft("fleetDetails.organizationId", value)}
              />
              <Field
                label="Fleet organization name"
                value={toInputValue(draft.fleetDetails?.organizationName)}
                onChange={(value) => updateDraft("fleetDetails.organizationName", value)}
              />
              <Field
                label="Fleet name"
                value={toInputValue(draft.fleetDetails?.fleetName)}
                onChange={(value) => updateDraft("fleetDetails.fleetName", value)}
              />
              <Field
                label="Fleet category"
                value={toInputValue(draft.fleetDetails?.fleetCategory)}
                onChange={(value) => updateDraft("fleetDetails.fleetCategory", value)}
              />
              <Field
                label="External reference"
                value={toInputValue(draft.fleetDetails?.externalRef)}
                onChange={(value) => updateDraft("fleetDetails.externalRef", value)}
              />
              <Field
                label="Member type"
                value={toInputValue(draft.fleetDetails?.memberType)}
                onChange={(value) => updateDraft("fleetDetails.memberType", value)}
              />
              <Field
                label="Assignee name"
                value={toInputValue(draft.fleetDetails?.assigneeName)}
                onChange={(value) => updateDraft("fleetDetails.assigneeName", value)}
              />
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-3">
          <Button
            disabled={!canWrite || pendingAction === `save:${selectedIncidentId}`}
            onClick={onSave}
            type="button"
          >
            {pendingAction === `save:${selectedIncidentId}` ? "Saving FNOL…" : "Save FNOL"}
          </Button>
        </div>
      </div>
    </div>
  )
}

function Field(props: {
  defaultValue?: string
  label: string
  name?: string
  onChange?: (value: string) => void
  options?: Array<{ label: string; value: string }>
  placeholder?: string
  select?: boolean
  type?: string
  value?: string
}) {
  return (
    <label className="grid gap-2 text-sm">
      <span className="font-medium">{props.label}</span>
      {props.select ? (
        <select
          className={INPUT_CLASS}
          value={props.value}
          onChange={(event) => props.onChange?.(event.target.value)}
        >
          {props.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : props.onChange ? (
        <input
          className={INPUT_CLASS}
          placeholder={props.placeholder}
          type={props.type}
          value={props.value}
          onChange={(event) => props.onChange?.(event.target.value)}
        />
      ) : (
        <input
          className={INPUT_CLASS}
          defaultValue={props.defaultValue}
          name={props.name}
          placeholder={props.placeholder}
          type={props.type}
        />
      )}
    </label>
  )
}

function TextareaField(props: {
  label: string
  onChange: (value: string) => void
  placeholder?: string
  value: string
}) {
  return (
    <label className="grid gap-2 text-sm">
      <span className="font-medium">{props.label}</span>
      <textarea
        className={TEXTAREA_CLASS}
        placeholder={props.placeholder}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </label>
  )
}

function CheckboxField(props: { label: string; name: string }) {
  return (
    <label className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background/85 px-3 py-2 text-sm">
      <input name={props.name} type="checkbox" />
      <span>{props.label}</span>
    </label>
  )
}
